import React, { useState } from 'react';
import { useAuth } from './context/AuthContext';
import Modal from './components/Modal'; // Assumindo que você já tem este componente de modal
import { useNavigate } from 'react-router-dom'; // Importe useNavigate

const UsersPage: React.FC = () => {
  const { users, addUser, updateUser, deleteUser, logout } = useAuth();
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [editingUser, setEditingUser] = useState<{ username: string; password?: string } | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [error, setError] = useState('');

  const navigate = useNavigate(); // Hook para navegação

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalOnConfirm, setModalOnConfirm] = useState<(() => void) | undefined>(undefined);
  const [modalShowConfirmButton, setModalShowConfirmButton] = useState(true);

  const openModal = (
    title: string,
    message: string,
    onConfirm?: () => void,
    showConfirmButton: boolean = true
  ) => {
    setModalTitle(title);
    setModalMessage(message);
    setModalOnConfirm(() => onConfirm);
    setModalShowConfirmButton(showConfirmButton);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalOnConfirm(undefined);
  };

  const handleModalConfirm = () => {
    if (modalOnConfirm) {
      modalOnConfirm();
    }
    closeModal();
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!newUsername || !newPassword) {
      setError('Nome de usuário e senha são obrigatórios.');
      return;
    }
    const success = await addUser(newUsername, newPassword);
    if (success) {
      setNewUsername('');
      setNewPassword('');
      openModal('Sucesso', 'Usuário adicionado com sucesso!', undefined, false);
    } else {
      setError('Nome de usuário já existe.');
    }
  };

  const handleEditClick = (user: { username: string; password?: string }) => {
    setEditingUser(user);
    setEditUsername(user.username);
    setEditPassword(user.password || ''); // Pré-preenche com a senha atual ou vazio
    setError('');
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!editUsername) {
      setError('Nome de usuário não pode ser vazio.');
      return;
    }
    if (editingUser) {
      const success = await updateUser(editingUser.username, editUsername, editPassword);
      if (success) {
        setEditingUser(null);
        setEditUsername('');
        setEditPassword('');
        openModal('Sucesso', 'Usuário atualizado com sucesso!', undefined, false);
      } else {
        setError('Erro ao atualizar usuário. O nome de usuário pode já existir.');
      }
    }
  };

  const handleDeleteUser = (username: string) => {
    openModal(
      'Confirmar Exclusão',
      `Tem certeza que deseja excluir o usuário "${username}"?`,
      async () => {
        await deleteUser(username);
        openModal('Sucesso', 'Usuário excluído com sucesso!', undefined, false);
      }
    );
  };

  return (
    <div className="min-h-screen bg-zinc-900 text-white p-4">
      <nav className="bg-zinc-800 p-4 shadow-lg flex items-center justify-between">
        {/* Logo Maurix adicionada e clicável */}
        <div className="flex items-center">
          <button
            onClick={() => navigate('/main')} // Navega para a MainPage ao clicar na logo
            className="focus:outline-none" // Remove o outline de foco padrão do botão
          >
            <img
              src="/maurix-logo.png"
              alt="Maurix Logo"
              className="h-10 mr-4"
              onError={(e) => {
                e.currentTarget.src = "https://placehold.co/40x40/cccccc/333333?text=Logo"; // Fallback
              }}
            />
          </button>
        </div>

        {/* Título "Gerenciar Usuários" centralizado */}
        <h1 className="text-3xl font-bold text-green-400 flex-grow text-center">Gerenciar Usuários</h1>

        {/* Botão de Logout */}
        <button
          onClick={logout}
          className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
        >
          Sair
        </button>
      </nav>

      <div className="max-w-4xl mx-auto mt-8">
        {/* Adicionar Novo Usuário */}
        <div className="bg-zinc-800 p-6 rounded-lg shadow-lg mb-8">
          <h2 className="text-2xl font-bold text-blue-400 mb-4">Adicionar Novo Usuário</h2>
          <form onSubmit={handleAddUser} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label htmlFor="newUsername" className="block text-white text-sm font-bold mb-2">
                Nome de Usuário:
              </label>
              <input
                type="text"
                id="newUsername"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-zinc-900 leading-tight focus:outline-none focus:shadow-outline"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="newPassword" className="block text-white text-sm font-bold mb-2">
                Senha:
              </label>
              <input
                type="password"
                id="newPassword"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-zinc-900 leading-tight focus:outline-none focus:shadow-outline"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              Adicionar Usuário
            </button>
            {error && <p className="text-red-500 text-xs italic mt-2 col-span-3 text-center">{error}</p>}
          </form>
        </div>

        {/* Lista de Usuários Existentes */}
        <div className="bg-zinc-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-purple-400 mb-4">Usuários Existentes</h2>
          {users.length === 0 ? (
            <p className="text-gray-400">Nenhum usuário cadastrado.</p>
          ) : (
            <ul className="divide-y divide-zinc-700">
              {users.map((user) => (
                <li key={user.username} className="py-4 flex flex-col md:flex-row justify-between items-center">
                  {editingUser && editingUser.username === user.username ? (
                    <form onSubmit={handleUpdateUser} className="flex flex-col md:flex-row items-center w-full">
                      <div className="flex-grow mb-2 md:mb-0 md:mr-4">
                        <input
                          type="text"
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-zinc-900 leading-tight focus:outline-none focus:shadow-outline"
                          value={editUsername}
                          onChange={(e) => setEditUsername(e.target.value)}
                          required
                        />
                      </div>
                      <div className="flex-grow mb-2 md:mb-0 md:mr-4">
                        <input
                          type="password"
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-zinc-900 leading-tight focus:outline-none focus:shadow-outline"
                          value={editPassword}
                          onChange={(e) => setEditPassword(e.target.value)}
                          placeholder="Nova Senha (opcional)"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <button
                          type="submit"
                          className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                        >
                          Salvar
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingUser(null)}
                          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                        >
                          Cancelar
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <span className="text-lg font-semibold">{user.username}</span>
                      <div className="flex space-x-2 mt-2 md:mt-0">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-1 px-3 rounded text-sm"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.username)}
                          className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded text-sm"
                        >
                          Excluir
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={handleModalConfirm}
        title={modalTitle}
        message={modalMessage}
        showConfirmButton={modalShowConfirmButton}
      />
    </div>
  );
};

export default UsersPage;
